import {
  RESTAURANT_ORDERS_AGGREGATION,
  RESTAURANT_MARKETING_SUITE,
  MIS_CAMPAIGNS_AUTOMATED_EMAIL,
  MIS_CAMPAIGNS_AUTOMATED_SMS,
  MIS_CAMPAIGNS_ONE_TIME_EMAIL,
  MIS_CAMPAIGNS_ONE_TIME_SMS,
  MIS_CSV_IMPORT,
} from './scopes';

export type ScopeDetails = {
  locationScopes?: string[];
  userScopes?: string[];
  misScopes?: string[];
  isUserCubohOnly: boolean;
};
type ScopeCondition = (scopeDetails?: ScopeDetails) => boolean;

/** LaunchDarkly flag set, as returned by `useFlags()`. */
export type FeatureFlags = Record<string, unknown>;

export const hasUserScope = (requiredScope: string) => (scopeDetails?: ScopeDetails) =>
  (scopeDetails?.userScopes ?? []).includes(requiredScope);

export const hasLocationScope = (requiredScope: string) => (scopeDetails?: ScopeDetails) =>
  (scopeDetails?.locationScopes ?? []).includes(requiredScope);

/** For nav items gated on a LaunchDarkly flag rather than a scope. */
export const hasFeatureFlag =
  (requiredFlag: string) => (_scopeDetails?: ScopeDetails, featureFlags?: FeatureFlags) =>
    !!featureFlags?.[requiredFlag];

/**
 * compose scope conditions
 *
 * ```js
 * hasUserScope("...") && hasUserScope("...") // is equivalent to
 * compose(hasUserScope("..."), hasUserScope("..."))
 * ```
 */
export const compose =
  (...predicates: ScopeCondition[]) =>
  (scopeDetails: ScopeDetails) =>
    predicates.every(predicate => predicate(scopeDetails));

export const canSeeAggregationPage = (
  scopeDetails: ScopeDetails,
  requiredScope: string = RESTAURANT_ORDERS_AGGREGATION,
) => {
  if (scopeDetails.isUserCubohOnly) {
    return true;
  }

  return hasLocationScope(requiredScope)(scopeDetails);
};

export const canSeeCampaignSettingsPage = (scopeDetails: ScopeDetails) =>
  hasLocationScope(RESTAURANT_MARKETING_SUITE)(scopeDetails);

export const canSeeCsvImport = (scopeDetails?: ScopeDetails) =>
  (scopeDetails?.misScopes ?? []).includes(MIS_CSV_IMPORT);

export const canSeeCampaignsPage = (scopeDetails: ScopeDetails) => {
  const isEntitled = hasLocationScope(RESTAURANT_MARKETING_SUITE)(scopeDetails);
  const misScopes = scopeDetails.misScopes ?? [];
  return (
    isEntitled &&
    (misScopes.includes(MIS_CAMPAIGNS_AUTOMATED_EMAIL) ||
      misScopes.includes(MIS_CAMPAIGNS_AUTOMATED_SMS))
  );
};

export const canSeeOneTimeCampaignsPage = (scopeDetails: ScopeDetails) => {
  const isEntitled = hasLocationScope(RESTAURANT_MARKETING_SUITE)(scopeDetails);
  const misScopes = scopeDetails.misScopes ?? [];
  return (
    isEntitled &&
    (misScopes.includes(MIS_CAMPAIGNS_ONE_TIME_EMAIL) ||
      misScopes.includes(MIS_CAMPAIGNS_ONE_TIME_SMS))
  );
};
